import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import type { MouseEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import type { Deployment } from "@/features/deployment-selection/model/deployment";
import {
  createDeploymentRoutePath,
  getDeploymentRouteSection,
} from "@/features/deployment-selection/utils/deployment-paths";
import {
  ArrowBackIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LocationIcon,
} from "@/shared/ui/console-icons";

interface SidebarDeploymentSwitcherProps {
  collapsed: boolean;
}

function formatStatus(status: string): string {
  return status.trim().replace(/[-_]+/g, " ");
}

function getDeploymentSubtitle(deployment: Deployment): string {
  return [deployment.deployment_slug, formatStatus(deployment.deployment_status)]
    .filter(Boolean)
    .join(" - ");
}

export function SidebarDeploymentSwitcher({ collapsed }: SidebarDeploymentSwitcherProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { deploymentId, selectedDeployment, deployments } = useSelectedDeployment();
  const location = useLocation();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const selectedSubtitle = getDeploymentSubtitle(selectedDeployment);

  function handleOpen(event: MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleBackToSelector() {
    navigate("/deployments");
  }

  function handleSelect(nextDeploymentId: string) {
    handleClose();

    if (nextDeploymentId === deploymentId) {
      return;
    }

    navigate(
      createDeploymentRoutePath(nextDeploymentId, getDeploymentRouteSection(location.pathname))
    );
  }

  const backButton = (
    <Tooltip
      title={collapsed ? "Back to deployments" : ""}
      placement="right"
      arrow
      disableHoverListener={!collapsed}
    >
      <IconButton
        onClick={handleBackToSelector}
        aria-label="Back to deployments"
        size="small"
        sx={(theme) => ({
          color: "text.secondary",
          "&:hover": {
            backgroundColor: alpha(theme.palette.common.white, 0.05),
            color: "text.primary",
          },
        })}
      >
        <ArrowBackIcon sx={{ fontSize: "1rem" }} />
      </IconButton>
    </Tooltip>
  );

  const button = (
    <ButtonBase
      onClick={handleOpen}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={`Deployment: ${selectedDeployment.deployment_name}`}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        width: "100%",
        px: collapsed ? 0 : 1.5,
        py: 1,
        gap: 1,
        justifyContent: collapsed ? "center" : "flex-start",
        borderRadius: `${(theme.shape.borderRadius as number) / 2}px`,
        color: "text.primary",
        "&:hover": {
          backgroundColor: alpha(theme.palette.common.white, 0.05),
        },
      })}
    >
      <LocationIcon sx={{ fontSize: "1rem", color: "text.secondary", flexShrink: 0 }} />
      {!collapsed && (
        <>
          <Box sx={{ flexGrow: 1, textAlign: "left", overflow: "hidden" }}>
            <Typography
              variant="subtitle2"
              sx={{
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedDeployment.deployment_name}
            </Typography>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                display: "block",
                lineHeight: 1.4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedSubtitle}
            </Typography>
          </Box>
          {open ? (
            <ChevronUpIcon sx={{ fontSize: "1rem", color: "text.secondary", flexShrink: 0 }} />
          ) : (
            <ChevronDownIcon sx={{ fontSize: "1rem", color: "text.secondary", flexShrink: 0 }} />
          )}
        </>
      )}
    </ButtonBase>
  );

  return (
    <Box sx={{ px: collapsed ? 0.5 : 1, py: 0.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: collapsed ? 0.5 : 1,
        }}
      >
        {backButton}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {collapsed ? (
            <Tooltip title={selectedDeployment.deployment_name} placement="right" arrow>
              {button}
            </Tooltip>
          ) : (
            button
          )}
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              width: 248,
              mt: 0.5,
              border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            }),
          },
        }}
      >
        <Box
          component="ul"
          role="listbox"
          aria-label="Select deployment"
          sx={{ py: 1, px: 0, m: 0, listStyle: "none" }}
        >
          {deployments.map((deployment) => (
            <Box key={deployment.deployment_id} component="li">
              <ButtonBase
                role="option"
                aria-selected={deployment.deployment_id === deploymentId}
                onClick={() => handleSelect(deployment.deployment_id)}
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  width: "100%",
                  px: 2,
                  py: 0.75,
                  gap: 1.5,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.common.white, 0.05),
                  },
                })}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: deployment.deployment_id === deploymentId ? 700 : 500,
                      color:
                        deployment.deployment_id === deploymentId
                          ? "text.primary"
                          : "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {deployment.deployment_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getDeploymentSubtitle(deployment)}
                  </Typography>
                </Box>
                {deployment.deployment_id === deploymentId && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        color: theme.palette.success.main,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      })}
                    >
                      Selected
                    </Typography>
                    <Box
                      aria-hidden="true"
                      sx={(theme) => ({
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.success.main,
                        flexShrink: 0,
                      })}
                    />
                  </Box>
                )}
              </ButtonBase>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}
